import React from 'react';
import { CourseDesign, DesignAsset } from '../../../services/designRunsService';
import { Card, Chip, TextArea, TextField } from './editors';

const KIND_TONES: Record<string, 'blue' | 'indigo' | 'green' | 'amber' | 'gray'> = {
  dialogue_audio: 'blue',
  narration_audio: 'blue',
  scenario_video: 'indigo',
  graphic: 'green',
  hero_image: 'green',
};

function TurnsEditor({ asset, editing, onChange }: {
  asset: DesignAsset;
  editing: boolean;
  onChange: (next: DesignAsset) => void;
}) {
  const turns = asset.turns ?? [];
  if (!editing) {
    return (
      <div className="text-base text-gray-700 space-y-0.5">
        {turns.map((t, i) => (
          <div key={i}><span className="font-medium">{t.speaker}:</span> {t.text}</div>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-1">
      {turns.map((t, i) => (
        <div key={i} className="flex gap-2 items-start">
          <input value={t.speaker} className="w-24 border border-gray-300 rounded px-1 py-0.5 text-sm"
            onChange={(e) => onChange({ ...asset, turns: turns.map((x, j) => j === i ? { ...x, speaker: e.target.value } : x) })} />
          <textarea value={t.text} rows={1} className="flex-1 border border-gray-300 rounded px-2 py-0.5 text-sm"
            onChange={(e) => onChange({ ...asset, turns: turns.map((x, j) => j === i ? { ...x, text: e.target.value } : x) })} />
          <button className="text-xs text-red-500 mt-1" onClick={() => onChange({ ...asset, turns: turns.filter((_, j) => j !== i) })}>✕</button>
        </div>
      ))}
      <button className="text-xs text-blue-600"
        onClick={() => onChange({ ...asset, turns: [...turns, { speaker: turns[0]?.speaker ?? 'A', text: '' }] })}>
        + turn
      </button>
    </div>
  );
}

/** All asset briefs: scripts, transcripts, image briefs. Editable when `editing`. */
export function DossierAssets({ design, editing, onChange }: {
  design: CourseDesign;
  editing: boolean;
  onChange: (next: CourseDesign) => void;
}) {
  if (design.assets.length === 0) {
    return <div className="text-sm text-gray-400">No assets designed yet.</div>;
  }
  const setAsset = (idx: number, next: DesignAsset) =>
    onChange({ ...design, assets: design.assets.map((a, i) => (i === idx ? next : a)) });

  return (
    <div className="space-y-2">
      {design.assets.map((asset, idx) => (
        <Card key={asset.asset_id}>
          <div className="flex items-center gap-2 mb-1.5">
            <Chip tone={KIND_TONES[asset.kind] ?? 'gray'}>{asset.kind}</Chip>
            <span className="text-sm text-gray-400 font-mono">{asset.asset_id}</span>
            {asset.speakers && (
              <span className="text-sm text-gray-500">
                {asset.speakers.map((s) => s.label).join(' + ')}
              </span>
            )}
          </div>
          {editing ? (
            <TextArea label="Brief" rows={2} value={asset.brief}
              onChange={(brief) => setAsset(idx, { ...asset, brief })} />
          ) : (
            <div className="text-sm text-gray-500 mb-1">{asset.brief}</div>
          )}
          {asset.turns && <TurnsEditor asset={asset} editing={editing} onChange={(a) => setAsset(idx, a)} />}
          {asset.transcript !== undefined && (
            editing ? (
              <TextArea label="Transcript (recorded verbatim)" rows={2} value={asset.transcript ?? ''}
                onChange={(transcript) => setAsset(idx, { ...asset, transcript })} />
            ) : (
              <div className="text-base text-gray-700 italic">“{asset.transcript}”</div>
            )
          )}
          {asset.image_brief !== undefined && (
            editing ? (
              <TextField label="Image brief" value={asset.image_brief ?? ''}
                onChange={(image_brief) => setAsset(idx, { ...asset, image_brief })} />
            ) : (
              asset.image_brief && <div className="text-sm text-gray-500">Image: {asset.image_brief}</div>
            )
          )}
          {asset.setting && <div className="text-sm text-gray-500 mt-1">Setting: {asset.setting}</div>}
        </Card>
      ))}
    </div>
  );
}
